import { useRef } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'
import Cached from '@mui/icons-material/Cached'
import Chip from '@mui/material/Chip'
import Typography from '@mui/material/Typography'
import {
    managedHostingGroupRoleLabel,
    managedHostingLocationLabel,
    managedHostingStatusLabels,
    managedHostingStatusTone,
} from 'common-billing-server/managedHosting'
import { ListManagedHostingSortableField, type ManagedHostingAdminDeployment } from 'common-billing-server/types'
import type { SortOrder } from 'common/api/v1/types'
import type { Query } from 'common/query'
import { useBillingApi } from '../../api/billing/billing-context'
import { formatDateTime } from '../../format-date'
import { makeEditCustomerPath, makeManagedHostingDetailsPath } from '../../routes/path'
import { type Column, DataFetchingPaginatedTable, type RefreshableDataFetchingPaginatedTable } from '../common/Table'
import { useFailedOperationBanner } from '../common/hooks/hook-failed-operation'
import type { PaginatedDataFetchingFilter } from '../common/hooks/hook-paginated-data-fetching'
import { RotatingButton } from '../common/RotatingButton'
import Wrapper from '../common/Wrapper'

const columns: Column<ManagedHostingAdminDeployment, ListManagedHostingSortableField>[] = [
    {
        title: 'Deployment',
        sortField: ListManagedHostingSortableField.name,
        valueForColumn: (deployment) => (
            <>
                {deployment.name}
                <Typography variant="body2" color="text.secondary">
                    {managedHostingGroupRoleLabel(deployment)}
                </Typography>
                <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                    {deployment.hostname}
                </Typography>
            </>
        ),
    },
    {
        title: 'Customer',
        sortField: ListManagedHostingSortableField.customerName,
        isClickable: false,
        valueForColumn: ({ customer }) => <Link to={makeEditCustomerPath(customer.id)}>{customer.name}</Link>,
    },
    {
        title: 'Location',
        sortField: ListManagedHostingSortableField.location,
        valueForColumn: ({ location }) => managedHostingLocationLabel(location),
    },
    {
        title: 'Version',
        sortField: ListManagedHostingSortableField.version,
        valueForColumn: ({ location }) => location.version,
    },
    {
        title: 'Status',
        sortField: ListManagedHostingSortableField.status,
        valueForColumn: ({ status }) => (
            <Chip size="small" label={managedHostingStatusLabels[status]} color={managedHostingStatusTone(status)} />
        ),
    },
    {
        title: 'Control plane',
        valueForColumn: ({ kind, controlPlaneHostname }) => (kind === 'connect' ? (controlPlaneHostname ?? '—') : '—'),
    },
    {
        title: 'Created at (UTC)',
        sortField: ListManagedHostingSortableField.createdAt,
        valueForColumn: ({ createdAt }) => formatDateTime(createdAt),
    },
]

export const ListManagedHostingPage = () => {
    const api = useBillingApi()
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const customerId = searchParams.get('customerId') || undefined
    const tableRef = useRef<RefreshableDataFetchingPaginatedTable>(null)
    const { addFailedOperation, removeFailedOperation, errorBanner } = useFailedOperationBanner()

    async function fetchDeployments({
        filter,
        ...query
    }: Query<PaginatedDataFetchingFilter<{ showDeleted: boolean }>, SortOrder<ListManagedHostingSortableField>>) {
        try {
            const result = await api.listManagedHostingDeployments({
                ...query,
                filter: {
                    customerId,
                    searchName: filter?.searchString,
                    excludeDeleted: !filter?.boolFilters?.showDeleted,
                },
            })
            removeFailedOperation('list-managed-hosting')
            return result
        } catch (error) {
            addFailedOperation({
                id: 'list-managed-hosting',
                message: 'Failed fetching managed hosting deployments',
                error: error as Error,
                retryFn: tableRef.current?.refreshData,
            })
            throw error
        }
    }

    return (
        <Wrapper
            fillHeight
            actions={
                <RotatingButton
                    key="refresh"
                    tooltip="Refresh"
                    onClick={() => tableRef.current?.refreshData()}
                    Component={<Cached />}
                />
            }
        >
            <DataFetchingPaginatedTable
                stickyHeader
                key={customerId}
                myRef={tableRef}
                api={fetchDeployments}
                columns={columns}
                boolFilters={[{ filterName: 'showDeleted', label: 'Show deleted deployments', initialValue: false }]}
                searchParameters={{}}
                emptyRowsMessage="No managed hosting deployments match the selected filters"
                onRowClicked={(deployment) => void navigate(makeManagedHostingDetailsPath(deployment.id))}
            />
            {errorBanner}
        </Wrapper>
    )
}
